import {
  AnalyticsFieldName,
  GroupClassSeriesAnalyticsEvent,
  GroupClassSeriesAnalyticsSources,
} from "@/constants/analytics/Analytics";
import { ExploreTestID, TextTestID } from "@/constants/TestID";
import type { OnCompletionCallback } from "@/hooks/useGroupClassRegistration";
import { useAnalytics } from "@/services/Analytics";
import RegisterButton from "@/ui/components/groupClasses/RegisterButton";
import { Text } from "@/ui/core";
import { BaseCard, Box, HStack, VStack } from "@/ui/layout";
import { getIllustrationIcon } from "@/ui/theme/icons/illustrations/groupClasses";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface GroupClassSeriesCardProps {
  attendeeTypes?: AttendeeType[];
  description: string;
  forWhom?: string;
  groupClassExternalId: string;
  iconName: string;
  isAppUserSupport?: boolean;
  isLoading?: boolean;
  isSensitive?: boolean;
  isUserRegistered: boolean;
  onSelect: (groupClassExternalId: string) => void;
  patientExternalId: string;
  recurrenceDisplayString: string;
  title: string;
}

const GroupClassSeriesCard = ({
  attendeeTypes,
  description,
  // this card wants to show "Register" by default, not "Register for group" by default
  // to ensure the latter, forWhom here defaults to "" explicitly
  forWhom = "",
  groupClassExternalId,
  iconName,
  isAppUserSupport,
  isLoading,
  isSensitive,
  isUserRegistered,
  onSelect,
  patientExternalId,
  recurrenceDisplayString,
  title,
}: GroupClassSeriesCardProps) => {
  const { track } = useAnalytics();

  const IllustrationIcon = getIllustrationIcon({ iconName });

  const baseTestID = joinTestIDs(
    groupClassExternalId,
    ExploreTestID.GroupClassSeriesCard,
  );

  const onPress = () => {
    onSelect(groupClassExternalId);
  };

  const onRegistrationChange: OnCompletionCallback = ({
    isRegistered,
    wasRegistered,
  }) => {
    const eventName = wasRegistered
      ? GroupClassSeriesAnalyticsEvent.UnregisterPressed
      : GroupClassSeriesAnalyticsEvent.RegisterPressed;
    track(eventName, {
      ...(attendeeTypes
        ? {
            attendeeTypes: JSON.stringify(
              // eslint-disable-next-line max-params
              [...attendeeTypes].sort((a, b) => a.localeCompare(b)),
            ),
          }
        : {}),
      [AnalyticsFieldName.Component]: GroupClassSeriesCard.name,
      [AnalyticsFieldName.GroupClassId]: groupClassExternalId,
      [AnalyticsFieldName.PatientId]: patientExternalId,
      [AnalyticsFieldName.Source]: GroupClassSeriesAnalyticsSources.Card,
      [AnalyticsFieldName.Success]: isRegistered !== wasRegistered,
      [AnalyticsFieldName.Title]: title,
    });
  };

  return (
    <BaseCard
      isDisabled={isLoading}
      onPress={onPress}
      testID={joinTestIDs(baseTestID, TextTestID.Card)}
    >
      <VStack
        gap={6}
        testID={joinTestIDs(baseTestID, TextTestID.Card, "touchable")}
      >
        <HStack justifyContent="space-between">
          {IllustrationIcon}
          <Box alignSelf="center">
            <RegisterButton
              forWhom={forWhom}
              groupClassExternalId={groupClassExternalId}
              isAppUserSupport={isAppUserSupport}
              isOnPressDisabled={isLoading}
              isRegistered={isUserRegistered}
              isSensitive={isSensitive}
              onRegistrationChange={onRegistrationChange}
              patientExternalId={patientExternalId}
              size="small"
              testID={joinTestIDs(baseTestID, ExploreTestID.RegisterButton)}
            />
          </Box>
        </HStack>
        <VStack gap={6}>
          <Text
            testID={joinTestIDs(baseTestID, TextTestID.Title)}
            variant="bodyMedium"
          >
            {title}
          </Text>
          <Text
            testID={joinTestIDs(baseTestID, TextTestID.Description)}
            variant="caption"
          >
            {description}
          </Text>
          <Text
            color={ColorToken.NeutralSecondary}
            testID={joinTestIDs(
              baseTestID,
              ExploreTestID.RecurrenceDisplayString,
            )}
            variant="caption"
          >
            {recurrenceDisplayString}
          </Text>
        </VStack>
      </VStack>
    </BaseCard>
  );
};

export type { GroupClassSeriesCardProps };
export default GroupClassSeriesCard;
